import "./content.css";
import { publicationOperations } from "./publicationOperations";
import { api, ApiError } from "../../api";
import { errorBanner, loadingBlock, pill } from "../shared/blocks";
import { IDEMPOTENCY_HEADER, intentFor, resolveIntent, peekIntent } from "../shared/intent";
import { createLifecycle, isAbort } from "../shared/lifecycle";
import { h, tidy } from "../shared/safe";
import { editorialStatus } from "../client/model";
import { busyButton, card, labelled, notice, select, textArea, textInput, webLink, whoText } from "../facts/kit";
import {
  CONTENT_STATUSES,
  CONTENT_STATUS_LABELS,
  PROVENANCE_CHOICES,
  PROVENANCE_CHOICE_LABELS,
  createBody,
  describeContentProblem,
  draftProblems,
  emptyDraft,
  fingerprint,
  isEditable,
  parseContentList,
  parseContentReply,
  provenanceLabel,
  splitKeywords,
  type ContentItem,
  type ContentProblem,
  type CreateDraft,
} from "./contentModel";
import type { PageInfo } from "../facts/factsModel";
import { clientWorkspaceNav } from "../client/workspaceNav";

// Supply content: #/clients/:id/content. The owner (or an AI assistant) supplies an article; CloudPush stores it as a DRAFT with its
// provenance and checks it against the constraint engine. CREATING IS NEVER APPROVING: approval is a separate, explicit step through the
// existing approve route (which re-runs the same check and has no override), and publishing is a third step on the client page.

const PAGE = 15;
const enc = encodeURIComponent;

export async function clientContentView(root: HTMLElement, params: Record<string, string>): Promise<void> {
  const shell = h("div", { class: "qf qf-cd fx ct" });
  root.replaceChildren(shell);
  const clientId = typeof params.id === "string" ? params.id : "";
  if (clientId === "") return void shell.append(errorBanner("This page needs a client. Go back to the Fleet and open one."));
  const life = createLifecycle(shell);
  const base = `/v1/clients/${enc(clientId)}`;

  shell.append(
    h("div", { class: "topbar" }, h("h1", { id: "ct-title" }, "Supply content"), h("div", { class: "btn-group" }, h("a", { class: "btn secondary", href: `#/clients/${enc(clientId)}` }, "← Client (approve and publish)"), h("a", { class: "btn secondary", href: `#/clients/${enc(clientId)}/links` }, "Created links"))),
    clientWorkspaceNav(clientId, "Content"),
    h("div", { class: "ct-banner", role: "note", id: "ct-banner" }, h("strong", null, "Creating content never approves it. "), "New content is saved as a draft and checked against the content rules. Approving is a separate decision, and the same check runs again then; publishing is a third step. Nothing here publishes anything."),
  );

  // ---- the create form (placed once and never moved) -------------------------------------------------------------------------------
  let draft: CreateDraft = emptyDraft();
  const result = h("div", { id: "ct-create-result", "aria-live": "polite" });
  const form = h("form", { class: "fx-form", novalidate: true, "aria-label": "Supply content", id: "ct-form" });
  const issuesShown: Record<string, string> = {};

  function fieldError(name: string): string | null {
    return issuesShown[name] ?? null;
  }
  function buildForm() {
    const set = <K extends keyof CreateDraft>(k: K) => (v: string) => {
      (draft[k] as string) = v;
      delete issuesShown[k as string];
      syncErrors();
    };
    const prov = select(PROVENANCE_CHOICES.map((p): [string, string] => [p, PROVENANCE_CHOICE_LABELS[p]]), draft.provenance, { name: "provenance", onChange: (v) => { draft.provenance = v as CreateDraft["provenance"]; syncErrors(); } });
    const create = busyButton({ label: "Save as draft", busyLabel: "Saving…", id: "ct-create", type: "submit", onClick: () => createIt() });
    form.onsubmit = (e) => {
      e.preventDefault();
      void create.click();
    };
    form.replaceChildren(
      h(
        "div",
        { class: "fx-grid" },
        labelled("Title", textInput(draft.title, { name: "title", onInput: set("title") }), { wide: true }),
        labelled("Content (HTML)", textArea(draft.body, { name: "body", rows: 10, onInput: set("body") }), { wide: true, help: "Must contain the client's claim block exactly as recorded. The check below tells you what is wrong." }),
        labelled("Where does it come from?", prov),
        labelled("Source address (required for imported content unless you add a note)", textInput(draft.sourceUrl, { name: "sourceUrl", placeholder: "https://", onInput: set("sourceUrl") })),
        labelled("Provenance note (optional)", textInput(draft.provenanceNote, { name: "provenanceNote", onInput: set("provenanceNote") }), { wide: true }),
        labelled("Topic (optional)", textInput(draft.topic, { name: "topic", onInput: set("topic") })),
        labelled("Keywords (optional, comma-separated)", textInput(draft.keywords, { name: "keywords", onInput: set("keywords") })),
      ),
      h("div", { class: "btn-group" }, create),
      result,
    );
  }
  function syncErrors() {
    for (const name of ["title", "body", "sourceUrl"]) {
      const input = form.querySelector<HTMLElement>(`[data-field="${name}"]`);
      const wrap = input?.closest(".fx-field");
      wrap?.querySelector(".fx-error")?.remove();
      input?.removeAttribute("aria-invalid");
      const msg = fieldError(name);
      if (msg && wrap) {
        wrap.append(h("div", { class: "fx-error", role: "alert" }, msg));
        input?.setAttribute("aria-invalid", "true");
      }
    }
  }

  function showProblem(p: ContentProblem, into: HTMLElement) {
    into.replaceChildren(
      notice(p.kind === "network" || p.kind === "server" ? "warn" : "bad", p.message, [...p.issues.map((i) => (i.field ? `${i.field}: ${i.message}` : i.message)), ...p.errors]),
    );
  }

  async function createIt() {
    result.replaceChildren();
    for (const k of Object.keys(issuesShown)) delete issuesShown[k];
    const local = draftProblems(draft);
    if (local.length) {
      for (const p of local) issuesShown[p.field] = p.message;
      syncErrors();
      result.replaceChildren(notice("bad", "Fix the highlighted fields first. Nothing was sent."));
      form.querySelector<HTMLElement>(`[data-field="${local[0].field}"]`)?.focus();
      return;
    }
    const body = createBody(draft);
    // One intention, one key: the same content resubmitted after an unreadable answer reuses its key (the server replays the original draft,
    // it cannot create a second one); edited content is a new request with a new key.
    const scope = `supply|${clientId}|${fingerprint(body)}`;
    const intent = intentFor(scope);
    try {
      const raw = await api<unknown>(`${base}/content`, { method: "POST", body: JSON.stringify(body), headers: { [IDEMPOTENCY_HEADER]: intent.key } });
      if (!life.alive()) return;
      const reply = parseContentReply(raw);
      if (!reply) {
        result.replaceChildren(notice("warn", "The server replied, but not with a draft this page recognises, so it is not known whether it was saved. Check the list below; saving again repeats the same request and cannot create a second draft."));
        return;
      }
      resolveIntent(scope);
      const c = reply.item;
      const passed = c.constraints?.passed === true;
      draft = emptyDraft();
      buildForm();
      form.querySelector("#ct-create-result")?.replaceChildren(
        notice(passed ? "ok" : "warn", `${reply.replayed ? "This request was already saved: showing the original draft. " : "Saved as a draft. "}It is NOT approved${passed ? ": it passes the content check and can be approved when you decide to" : ": it fails the content check, see below, and cannot be approved until it is revised"}.`, c.constraints && !passed ? c.constraints.errors : undefined),
      );
      offset = 0;
      await loadList();
      listHost.querySelector<HTMLElement>(`[data-content-id="${c.id}"] h3`)?.setAttribute("tabindex", "-1");
      listHost.querySelector<HTMLElement>(`[data-content-id="${c.id}"] h3`)?.focus();
    } catch (e) {
      if (!life.alive() || isAbort(e)) return;
      const p = describeContentProblem(e, "Not saved");
      if (p.definite) resolveIntent(scope);
      for (const i of p.issues) if (["title", "body", "sourceUrl", "provenanceNote"].includes(i.field)) issuesShown[i.field === "provenanceNote" ? "sourceUrl" : i.field] = i.message;
      syncErrors();
      showProblem(p, result);
      if (!p.definite && peekIntent(scope)) result.append(h("p", { class: "muted" }, "Saving again repeats the same request with the same key, so it cannot create a second draft."));
    }
  }

  // ---- the list ---------------------------------------------------------------------------------------------------------------------
  let items: ContentItem[] | null = null;
  let page: PageInfo | null = null;
  let offset = 0;
  let statusFilter = "all";
  let loadError: string | null = null;
  const flash = h("div", { "aria-live": "polite", id: "ct-flash" });
  const listHost = h("div", { id: "ct-list" });
  const filterSel = select(CONTENT_STATUSES.map((s): [string, string] => [s, CONTENT_STATUS_LABELS[s]]), "all", { name: "status", onChange: (v) => { statusFilter = v; offset = 0; void loadList(); } });
  filterSel.id = "ct-status";
  const prev = h("button", { type: "button", class: "secondary", id: "ct-prev", on: { click: () => void go(offset - PAGE) } }, "← Newer");
  const next = h("button", { type: "button", class: "secondary", id: "ct-next", on: { click: () => void go(offset + PAGE) } }, "Older →");
  const range = h("span", { class: "qf-pager__note", id: "ct-range", role: "status", "aria-live": "polite" });
  const nav = h("nav", { class: "qf-pager", "aria-label": "Content pages" }, prev, range, next);
  let seq = 0;
  /** Which item has its revise form or its approve confirmation open. */
  let revising: string | null = null;
  let approving: { id: string; problem: ContentProblem | null } | null = null;

  async function go(n: number) {
    if (n < 0 || (page && n >= page.total)) return;
    offset = n;
    await loadList();
  }

  async function loadList() {
    const mine = ++seq;
    const qs = new URLSearchParams({ limit: String(PAGE), offset: String(offset) });
    if (statusFilter !== "all") qs.set("status", statusFilter);
    try {
      const r = parseContentList(await api<unknown>(`${base}/content?${qs}`));
      if (mine !== seq || !life.alive()) return;
      items = r?.items ?? null;
      page = r?.page ?? null;
      loadError = r ? null : "The server sent a list this page does not understand.";
    } catch (e) {
      if (mine !== seq || !life.alive() || isAbort(e)) return;
      loadError = e instanceof ApiError ? (e.status === 404 ? "This client was not found." : `The content could not be loaded (HTTP ${e.status}).`) : "Couldn't reach the server.";
    }
    paintList();
  }

  function constraintBlock(c: ContentItem): HTMLElement {
    const k = c.constraints;
    if (!k) return h("div", { class: "ct-check", dataset: { constraints: "unknown" } }, h("strong", null, "Content check: "), "the server did not report a result, so this cannot be approved from here.");
    return h(
      "div",
      { class: `ct-check ${k.passed ? "ct-check--ok" : "ct-check--bad"}`, dataset: { constraints: k.passed ? "passed" : "failed" } },
      h("strong", null, "Content check against the client's current facts: "),
      k.passed ? "passed." : `fails ${k.errors.length} ${k.errors.length === 1 ? "rule" : "rules"}:`,
      !k.passed ? h("ul", null, ...k.errors.map((e) => h("li", null, e))) : null,
      k.warnings.length ? h("ul", { class: "muted" }, ...k.warnings.map((w) => h("li", null, `Warning: ${w}`))) : null,
    );
  }

  function itemBlock(c: ContentItem): HTMLElement {
    const ed = editorialStatus(c.status);
    const li = h(
      "li",
      { class: "ct-item", dataset: { contentId: c.id, status: c.status, provenance: c.provenance.kind } },
      h("h3", null, c.title),
      h("div", { class: "fx-unr-head" }, pill(ed.label, ed.tone), h("span", { class: "tag", dataset: { provenanceTag: "true" } }, provenanceLabel(c.provenance.kind)), c.createdAt ? h("span", { class: "muted" }, c.createdAt.text) : null),
      h("div", { class: "muted fx-small" }, `Supplied by ${whoText(c.provenance.suppliedBy)}${c.provenance.identityVerified === false ? " (identity not verified)" : ""}${c.provenance.model ? ` · model ${c.provenance.model}` : ""}${c.provenance.note ? ` · ${c.provenance.note}` : ""}`),
      c.provenance.sourceUrl ? h("div", { class: "muted fx-small" }, "Source: ", webLink(c.provenance.sourceUrl)) : null,
      c.topic ? h("div", { class: "muted fx-small" }, `Topic: ${c.topic.title}${c.topic.keywords.length ? ` · keywords: ${c.topic.keywords.join(", ")}` : ""}`) : null,
      constraintBlock(c),
      h("details", { class: "ct-body" }, h("summary", null, "Show the content"), h("div", { class: "ct-text", tabindex: "0", role: "region", "aria-label": `Content of ${tidy(c.title, 80)}` }, c.body || "(empty)")),
    );
    if (c.approval && (c.status === "approved" || c.status === "published")) li.append(h("div", { class: "muted fx-small", dataset: { approval: "true" } }, `Approved by ${whoText(c.approval.approvedBy)}${c.approval.approvedAt ? ` · ${c.approval.approvedAt.text}` : ""}. Approved content cannot be edited.`));
    if (isEditable(c.status)) {
      if (revising === c.id) li.append(reviseForm(c));
      else if (approving?.id === c.id) li.append(approveConfirm(c));
      else
        li.append(
          h(
            "div",
            { class: "btn-group ct-actions" },
            h("button", { type: "button", class: "secondary", dataset: { action: "revise" }, "aria-label": `Revise ${tidy(c.title, 80)}`, on: { click: () => { revising = c.id; approving = null; flash.replaceChildren(); paintList(); listHost.querySelector<HTMLElement>(`[data-content-id="${c.id}"] input`)?.focus(); } } }, "Revise"),
            c.constraints?.passed === true
              ? h("button", { type: "button", dataset: { action: "approve" }, "aria-label": `Approve ${tidy(c.title, 80)}`, on: { click: () => { approving = { id: c.id, problem: null }; revising = null; flash.replaceChildren(); paintList(); listHost.querySelector<HTMLElement>(`[data-content-id="${c.id}"] [data-confirm]`)?.focus(); } } }, "Approve…")
              : h("span", { class: "muted ct-cannot", dataset: { cannotApprove: "true" } }, "It cannot be approved until it passes the content check. Revise it first."),
          ),
        );
    } else if (c.status === "approved") {
      li.append(h("div", { class: "muted fx-small" }, "Approved, not published. ", h("a", { href: `#/clients/${enc(clientId)}` }, "Publish it from the client page"), "."));
    }
    return li;
  }

  function reviseForm(c: ContentItem): HTMLElement {
    const v = { title: c.title, body: c.body, topic: c.topic?.title ?? "", keywords: (c.topic?.keywords ?? []).join(", ") };
    const out = h("div", { "aria-live": "polite" });
    const topicFields = c.topic
      ? [
          labelled("Topic", textInput(v.topic, { name: "topic", onInput: (x) => void (v.topic = x) })),
          labelled("Keywords (comma-separated)", textInput(v.keywords, { name: "keywords", onInput: (x) => void (v.keywords = x) })),
        ]
      : [h("p", { class: "muted fx-small", role: "note" }, "This draft has no topic metadata. A revision can change its title and content, but cannot add a topic or keywords.")];
    const save = busyButton({
      label: "Save revision",
      busyLabel: "Saving…",
      id: `ct-revise-save-${c.id}`,
      onClick: async () => {
        const patch: Record<string, unknown> = {};
        if (v.title.trim() !== c.title) patch.title = v.title.trim();
        if (v.body !== c.body) patch.body = v.body;
        if (v.topic.trim() !== (c.topic?.title ?? "")) patch.topic = v.topic.trim();
        const kw = splitKeywords(v.keywords);
        if (JSON.stringify(kw) !== JSON.stringify(c.topic?.keywords ?? [])) patch.keywords = kw;
        if (Object.keys(patch).length === 0) return void out.replaceChildren(notice("info", "Nothing changed."));
        if (patch.title === "" || patch.body === "") return void out.replaceChildren(notice("bad", "The title and the content cannot be empty. Nothing was saved."));
        try {
          const reply = parseContentReply(await api<unknown>(`${base}/content/${enc(c.id)}`, { method: "PATCH", body: JSON.stringify(patch) }));
          if (!life.alive()) return;
          revising = null;
          flash.replaceChildren(notice(reply?.item.constraints?.passed ? "ok" : "warn", `Revision saved. It is a draft again and is NOT approved${reply?.item.constraints?.passed ? "; it passes the content check now" : "; it still fails the content check (see its card)"}.`));
          await loadList();
        } catch (e) {
          if (!life.alive() || isAbort(e)) return;
          const p = describeContentProblem(e, "Revision not saved");
          if (p.kind === "not_editable") {
            // it was approved (or published) meanwhile: the form has no purpose any more; say so and show the card as it is now
            revising = null;
            flash.replaceChildren(notice("warn", p.message));
            void loadList();
          } else showProblem(p, out);
        }
      },
    });
    return h(
      "form",
      { class: "fx-form ct-revise", novalidate: true, "aria-label": `Revise ${tidy(c.title, 80)}`, on: { submit: (e: Event) => { e.preventDefault(); void save.click(); } } },
      h("div", { class: "fx-grid" }, labelled("Title", textInput(v.title, { name: "title", onInput: (x) => void (v.title = x) }), { wide: true }), labelled("Content (HTML)", textArea(v.body, { name: "body", rows: 10, onInput: (x) => void (v.body = x) }), { wide: true }), ...topicFields),
      out,
      h("div", { class: "btn-group" }, save, h("button", { type: "button", class: "secondary", on: { click: () => { revising = null; paintList(); } } }, "Cancel")),
    );
  }

  /** Approval is its own explicit step: it says what it does and does not do, then asks. */
  function approveConfirm(c: ContentItem): HTMLElement {
    const prob = approving?.problem ?? null;
    const go = busyButton({
      label: "Confirm: approve this content",
      busyLabel: "Approving…",
      id: `ct-approve-confirm-${c.id}`,
      onClick: async () => {
        try {
          // ?clientId= makes the server refuse (404) a master that belongs to another client
          await api(`/v1/content/masters/${enc(c.id)}/approve?clientId=${enc(clientId)}`, { method: "POST" });
          if (!life.alive()) return;
          approving = null;
          flash.replaceChildren(notice("ok", "Approved. It has NOT been published: publishing is a separate step on the client page."));
          await loadList();
        } catch (e) {
          if (!life.alive() || isAbort(e)) return;
          const problem = describeContentProblem(e, "Not approved");
          approving = { id: c.id, problem };
          // the check is run again on approval, against the facts as they are now: show what it found, and refresh the card
          if (problem.kind === "constraints" || problem.kind === "notfound") await loadList();
          else paintList();
        }
      },
    });
    go.dataset.confirm = "true";
    const box = h(
      "div",
      { class: "fx-promote ct-approve", role: "group", "aria-label": `Approve ${tidy(c.title, 80)}` },
      h("strong", null, "Approve this content?"),
      h("p", null, "Approving is an editorial decision. The content check runs again now, against the client's current facts, and there is no override. Approving does not publish anything."),
      ...(prob ? [notice(prob.kind === "constraints" ? "bad" : "warn", prob.message, [...prob.issues.map((i) => i.message), ...prob.errors])] : []),
      h("div", { class: "btn-group" }, prob?.kind === "constraints" ? null : go, h("button", { type: "button", class: "secondary", on: { click: () => { approving = null; paintList(); } } }, prob?.kind === "constraints" ? "Close" : "Cancel")),
    );
    return box;
  }

  function paintList() {
    const total = page?.total ?? items?.length ?? 0;
    nav.hidden = !page || total <= PAGE;
    prev.setAttribute("aria-disabled", String(offset <= 0));
    next.setAttribute("aria-disabled", String(!page || offset + (items?.length ?? 0) >= total));
    range.textContent = items && items.length ? `${offset + 1}-${offset + items.length} of ${total}` : "";
    listHost.replaceChildren(
      loadError
        ? h("div", { class: "error-banner", role: "alert" }, loadError, " ", h("button", { type: "button", class: "secondary", on: { click: () => void loadList() } }, "Try again"))
        : items === null
          ? loadingBlock("Loading content", 3)
          : items.length === 0
            ? h("p", { class: "muted" }, statusFilter === "all" ? "No content yet. Supply the first article above." : "No content with that status.")
            : h("ul", { class: "ct-list", "aria-label": "Content" }, ...items.map(itemBlock)),
    );
  }

  buildForm();
  shell.append(
    card("ct-supply", "Supply an article", h("p", { class: "muted" }, "Paste or write the article. It is saved as a draft; nothing is approved or published."), form),
    card("ct-content", "This client's content", h("div", { class: "fx-bar" }, h("label", { for: "ct-status" }, "Status"), filterSel), flash, listHost, nav),
  );
  shell.append(publicationOperations(clientId));
  paintList();
  await loadList();
}
